import { useState } from 'react'
import {
    useArchiveClassifierDetailMutation,
    useCreateClassifierPromptMutation,
    useDiscardClassifierPromptDraftMutation,
    useGoLiveClassifierPromptMutation,
    useUnarchiveClassifierDetailMutation,
    useUpdateClassifierDetailMutation,
    useUpdateClassifierPromptMutation
} from '../../../api/client'
import { DEFAULT_LLM_MODEL } from '../../../models/LlmModel'
import { errorMessage } from '../../../util/errorMessage'
import { ClassifierMetadata } from './ClassifierMetadataFields'
import { DraftFormValues } from './ClassifierVersionPanel'
import {
    ClassifierPromptVersion,
    DEFAULT_BORDERLINE_THRESHOLD,
    DEFAULT_CONFIRMED_THRESHOLD,
    thresholdError
} from './classifierVersions'

type Options = {
    classifierDetailsId: number
    refetchDetails: () => Promise<unknown>
    refetchVersions: () => Promise<unknown>
}

/**
 * Every mutation the classifier editor can fire, behind a single error banner.
 *
 * Concentrating the network side effects here is what lets the sections of the page
 * stay presentational: they report what the admin asked for and never touch Apollo.
 * Each action resolves to null when it failed, so callers can hold off on the follow-up
 * UI change (reselecting a version, say) that only makes sense after a success.
 */
export function useClassifierEditActions({
    classifierDetailsId,
    refetchDetails,
    refetchVersions
}: Options) {
    const [error, setError] = useState<string | null>(null)

    const [updateDetail, { loading: savingDetail }] = useUpdateClassifierDetailMutation()
    const [updatePrompt, { loading: savingDraft }] = useUpdateClassifierPromptMutation()
    const [discardPrompt, { loading: discarding }] = useDiscardClassifierPromptDraftMutation()
    const [goLivePrompt, { loading: goingLive }] = useGoLiveClassifierPromptMutation()
    const [createPrompt, { loading: creatingDraft }] = useCreateClassifierPromptMutation()
    const [archiveClassifier, { loading: archiving }] = useArchiveClassifierDetailMutation()
    const [unarchiveClassifier, { loading: unarchiving }] = useUnarchiveClassifierDetailMutation()

    async function run<T>(work: () => Promise<T>): Promise<T | null> {
        setError(null)
        try {
            return await work()
        } catch (err) {
            setError(errorMessage(err))
            return null
        }
    }

    const saveDetail = (value: ClassifierMetadata) =>
        run(async () => {
            await updateDetail({ variables: { input: { id: classifierDetailsId, ...value } } })
            await refetchDetails()
            return true
        })

    const saveDraft = (versionId: number, values: DraftFormValues) => {
        const invalid = thresholdError(values.borderlineThreshold, values.confirmedThreshold)
        if (invalid) {
            setError(invalid)
            return Promise.resolve(null)
        }
        return run(async () => {
            await updatePrompt({
                variables: {
                    input: {
                        id: versionId,
                        prompt: values.prompt,
                        model: values.model,
                        defaultBorderlineThreshold: values.borderlineThreshold,
                        defaultConfirmedThreshold: values.confirmedThreshold
                    }
                }
            })
            await refetchVersions()
            return true
        })
    }

    const discardDraft = (versionId: number) =>
        run(async () => {
            await discardPrompt({ variables: { id: versionId } })
            await refetchVersions()
            return true
        })

    /**
     * `base` seeds the new draft so authors start from the current wording rather than
     * a blank prompt — including thresholds the base deliberately leaves unset, so the
     * defaults only apply to a brand new classifier. Resolves to the new version's id.
     */
    const createDraft = (base?: ClassifierPromptVersion) =>
        run(async () => {
            const res = await createPrompt({
                variables: {
                    input: {
                        classifierDetailsId,
                        prompt: base?.prompt ?? '',
                        model: base?.model ?? DEFAULT_LLM_MODEL,
                        defaultBorderlineThreshold: base
                            ? base.defaultBorderlineThreshold
                            : DEFAULT_BORDERLINE_THRESHOLD,
                        defaultConfirmedThreshold: base
                            ? base.defaultConfirmedThreshold
                            : DEFAULT_CONFIRMED_THRESHOLD
                    }
                }
            })
            const created = res.data?.createClassifierPrompt
            await refetchVersions()
            return created?.id ?? null
        })

    const goLive = (versionId: number) =>
        run(async () => {
            await goLivePrompt({ variables: { id: versionId } })
            await refetchVersions()
            return true
        })

    const archive = () =>
        run(async () => {
            await archiveClassifier({ variables: { id: classifierDetailsId } })
            await refetchDetails()
            return true
        })

    const unarchive = () =>
        run(async () => {
            await unarchiveClassifier({ variables: { id: classifierDetailsId } })
            await refetchDetails()
            return true
        })

    return {
        error,
        saveDetail,
        saveDraft,
        discardDraft,
        createDraft,
        goLive,
        archive,
        unarchive,
        savingDetail,
        savingDraft,
        discarding,
        goingLive,
        creatingDraft,
        archiving,
        unarchiving
    }
}
